import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Classroom, ClassroomBody } from '@innovamat/glow-api-client';
import { useGetCurrentWeekPeriodQuery } from '@innovamat/glow-api-client';
import { dates, newId } from '@innovamat/radiance-utils';

import { useOrganization } from '../classrooms-management';
import { useClassroomScheduleValues } from '../classrooms-management/hooks/use-classroom-schedule-values';
import { usePutClassroom } from '../classrooms-management/hooks/use-put-classroom';
import { getTimezone } from '../shared/modules/organization/organization';
import { is12hClock } from '../shared/modules/region/region-parameters';
import { useUser } from '../user-management';

type Props = {
  classroom: Classroom | undefined;
  onClose: () => void;
  onConfigurationChange?: () => void;
};

interface UseModifyDigitalPracticeModalReturn {
  configuredDate: {
    digitalPracticeFirstDayOfWeek: string | null | undefined;
    digitalPracticeFirstHourOfWeek: string | null | undefined;
  };
  daysOfWeek: dates.DayOfWeek[];
  isPending: boolean;
  nextOccurrence: Date | null;
  currentPeriodDate: { date: Date; timezone: string } | null;
  currentWeekPeriod: {
    start: { date: string; timezone: string };
    end: { date: string; timezone: string };
  } | null;
  hasChanges: boolean;
  formatDate: (date: Date, timezone?: string) => string;
  formatTime: ({
    date,
    clock,
    timezone,
  }: {
    date?: Date;
    clock?: string;
    timezone?: string;
  }) => string;
  handleOnChangeSelect: (value: string | undefined, key: string) => void;
  handleOnClose: () => void;
  handleOnSave: () => void;
  timezone: string | undefined;
}

const calculateDaysUntilNextWeekday = (
  currentDay: number,
  targetDay: number,
  currentHour: number,
  targetHour: number
): number => {
  let daysUntilTarget = targetDay - currentDay;
  if (daysUntilTarget === 0 && currentHour < targetHour) {
    daysUntilTarget = 0;
  } else if (daysUntilTarget <= 0) {
    daysUntilTarget += 7;
  }
  return daysUntilTarget;
};

const useModifyDigitalPracticeModal = ({
  classroom,
  onClose,
  onConfigurationChange,
}: Props): UseModifyDigitalPracticeModalReturn => {
  const { t } = useTranslation();
  const schedule = useClassroomScheduleValues();
  const { user } = useUser();
  const { organization } = useOrganization();
  const queryClient = useQueryClient();

  const [configuredDate, setConfiguredDate] = useState({
    digitalPracticeFirstDayOfWeek: classroom?.digitalPracticeFirstDayOfWeek,
    digitalPracticeFirstHourOfWeek: classroom?.digitalPracticeFirstHourOfWeek,
  });

  const { data: currentWeekPeriod } = useGetCurrentWeekPeriodQuery(
    { classroomId: classroom?.id || '' },
    { enabled: !!classroom?.id }
  );

  const orgTimezone = organization ? getTimezone(organization) : undefined;
  const daysOfWeek = dates.getDaysOfWeek(t);

  const getNextWeekdayOccurrence = (
    weekday: string | null | undefined,
    hour: string | null | undefined
  ): Date | null => {
    if (!weekday) return null;

    const targetDay = daysOfWeek.find(
      (day) => day.value === weekday
    )?.numericDay;
    if (targetDay === undefined) return null;

    const now = DateTime.now().setZone(orgTimezone);
    const todayDay = now.weekday % 7;
    const currentHour = now.hour;

    const targetHour = hour ? parseInt(hour.split(':')[0], 10) : 0;

    const daysUntilNext = calculateDaysUntilNextWeekday(
      todayDay,
      targetDay,
      currentHour,
      targetHour
    );

    const nextOccurrence = now.plus({ days: daysUntilNext }).set({
      hour: targetHour,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

    return nextOccurrence.toJSDate();
  };

  const nextOccurrence = getNextWeekdayOccurrence(
    configuredDate.digitalPracticeFirstDayOfWeek,
    configuredDate.digitalPracticeFirstHourOfWeek
  );

  const handleOnChangeSelect = (
    value: string | undefined,
    key: string
  ): void => {
    setConfiguredDate((prev) => ({ ...prev, [key]: value }));
  };

  const handleOnClose = (): void => {
    setConfiguredDate({
      digitalPracticeFirstDayOfWeek: classroom?.digitalPracticeFirstDayOfWeek,
      digitalPracticeFirstHourOfWeek: classroom?.digitalPracticeFirstHourOfWeek,
    });
    onClose();
  };

  const handleOnSave = (): void => {
    if (isPending) {
      return;
    }

    const body: ClassroomBody = {
      id: classroom?.id || newId(),
      icon: classroom?.icon || 0,
      organizationId: user?.organizationId!,
      level: classroom?.level,
      name: classroom?.name,
      courseId: classroom?.courseId,
      avatar: classroom?.avatar,
      scheduleEnd: classroom?.scheduleEnd,
      scheduleStart: classroom?.scheduleStart,
      type: classroom?.type,
      ...configuredDate,
    };

    handlePutClassroom(body, schedule.values.timeZone!);
  };

  const { handlePutClassroom, isPending } = usePutClassroom({
    onError: () => {},
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Classroom'] });
      queryClient.invalidateQueries({
        queryKey: ['getCurrentWeekPeriod', { classroomId: classroom?.id }],
      });
      onConfigurationChange?.();
      handleOnClose();
    },
  });

  const formatDate = (date: Date, timezone?: string): string => {
    const dateTime = timezone
      ? DateTime.fromJSDate(date).setZone(timezone)
      : DateTime.fromJSDate(date);

    const weekdayNum = dateTime.weekday % 7;
    const translatedWeekday = daysOfWeek.find(
      (day) => day.numericDay === weekdayNum
    )?.label;
    const dayNumber = dateTime.day;
    return `${translatedWeekday} ${dayNumber}`;
  };

  const formatTime = ({
    date,
    clock,
    timezone,
  }: {
    date?: Date;
    clock?: string;
    timezone?: string;
  }): string => {
    let dateTime: string | Date = date || dates.DEFAULT_TIME;

    if (!date) {
      dateTime =
        configuredDate.digitalPracticeFirstHourOfWeek || dates.DEFAULT_TIME;
    }

    return dates.formatTime({
      dateTime,
      hour12: is12hClock(clock),
      timezone,
    });
  };

  const getCurrentPeriodDate = (): { date: Date; timezone: string } | null => {
    if (currentWeekPeriod?.getCurrentWeekPeriod) {
      const { start } = currentWeekPeriod.getCurrentWeekPeriod;
      return {
        date: DateTime.fromSQL(start.date, { zone: start.timezone }).toJSDate(),
        timezone: start.timezone,
      };
    }
    return null;
  };

  const hasChanges =
    configuredDate.digitalPracticeFirstDayOfWeek !==
      classroom?.digitalPracticeFirstDayOfWeek ||
    configuredDate.digitalPracticeFirstHourOfWeek !==
      classroom?.digitalPracticeFirstHourOfWeek;

  return {
    configuredDate,
    daysOfWeek,
    isPending,
    nextOccurrence,
    currentPeriodDate: getCurrentPeriodDate(),
    currentWeekPeriod: currentWeekPeriod?.getCurrentWeekPeriod || null,
    hasChanges,
    formatDate,
    formatTime,
    handleOnChangeSelect,
    handleOnClose,
    handleOnSave,
    timezone: orgTimezone,
  };
};

export default useModifyDigitalPracticeModal;
